'use client';

import { useState } from 'react';
import { tw } from '@tw';
import { clampText } from '@/lib/utils/clamp-text';

/**
 * A line of the card that is also the field for that line — the same contract
 * the creator's name and tagline wear (`EditableText`), in the product card's colours.
 *
 * The node is UNCONTROLLED: React must never write into an element someone is
 * typing in, or the caret jumps to the end on every keystroke. Nothing here
 * saves; typing reports upward and the products store owns the one debounce,
 * because the whole catalogue is one write.
 */
export function InlineText({
  defaultValue,
  limit,
  placeholder,
  multiline = false,
  className,
  ariaLabel,
  onInput,
  onFocus,
  onBlur,
}: {
  /** Read once, on mount. Nothing may write into the node after that. */
  defaultValue: string;
  limit: number;
  placeholder: string;
  multiline?: boolean;
  className?: string;
  ariaLabel: string;
  onInput: (next: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  // Rendered once and never again: passing a value that moves while someone
  // types is what makes React rewrite the node and throw the caret to the end.
  const [initial] = useState(defaultValue);

  return (
    <span
      contentEditable="plaintext-only"
      suppressContentEditableWarning
      role="textbox"
      aria-label={ariaLabel}
      aria-multiline={multiline || undefined}
      spellCheck={false}
      onInput={(e) => onInput(clampText(e.currentTarget, limit))}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={(e) => {
        // A single thought; a newline in it has no meaning, and blurring ends the edit.
        if (e.key !== 'Enter' || e.shiftKey) return;
        e.preventDefault();
        e.currentTarget.blur();
      }}
      className={tw(
        'block cursor-text outline-none',
        'empty:before:text-[var(--sf-fnt)] empty:before:content-[attr(data-placeholder)]',
        className
      )}
      data-placeholder={placeholder}
    >
      {initial}
    </span>
  );
}
